import { FC, Fragment, useState } from "react";
import { Send } from "@mui/icons-material";

import { useMetamask, useWriteContractWithSimulation } from "@dzambalaorg/react-hooks-eth";
import { ListAction, ListActionVariant } from "@framework/styled";
import { AddressType, ContractFeatures, type IToken, TokenStatus } from "@framework/types";
import ERC20TransferFromABI from "@framework/abis/json/ERC20Simple/transfer.json";

import { ITransferDto, TransferDialog } from "../../../../dialogs/transfer";

interface IErc20TransferButtonProps {
  className?: string;
  disabled?: boolean;
  token: IToken;
  variant?: ListActionVariant;
}

export const Erc20TransferButton: FC<IErc20TransferButtonProps> = props => {
  const { className, disabled, token, variant = ListActionVariant.button } = props;

  const [isTransferTokenDialogOpen, setIsTransferTokenDialogOpen] = useState(false);

  const { writeContractAsync } = useWriteContractWithSimulation();

  const { execute: metaFn } = useMetamask(async (dto: ITransferDto) => {
    return writeContractAsync({
      address: token.template!.contract!.address as AddressType,
      abi: ERC20TransferFromABI,
      functionName: "transfer",
      args: [dto.account, dto.amount],
    });
  });

  const handleTransfer = (): void => {
    setIsTransferTokenDialogOpen(true);
  };

  const handleTransferConfirm = async (dto: ITransferDto) => {
    await metaFn(dto).finally(() => {
      setIsTransferTokenDialogOpen(false);
    });
  };

  const handleTransferCancel = () => {
    setIsTransferTokenDialogOpen(false);
  };

  if (token.tokenStatus === TokenStatus.BURNED) {
    return null;
  }

  return (
    <Fragment>
      <ListAction
        icon={Send}
        onClick={handleTransfer}
        message="form.buttons.transfer"
        className={className}
        dataTestId="Erc20TransferButton"
        disabled={disabled || token.template?.contract?.contractFeatures.includes(ContractFeatures.SOULBOUND)}
        variant={variant}
      />
      <TransferDialog
        onConfirm={handleTransferConfirm}
        onCancel={handleTransferCancel}
        open={isTransferTokenDialogOpen}
        message="dialogs.transfer"
        testId="Erc20TransferDialogForm"
        initialValues={{
          account: "",
          amount: "",
          decimals: token.template!.contract!.decimals,
        }}
      />
    </Fragment>
  );
};
